import classNames from 'classnames'
import styles from './DailySummary.module.css'
import { DailySummaryParam } from './DailySummaryParam'
import { RecommendationsButton } from './RecommendationsButton'
import { findClosestDate } from 'shared/lib/findClosestDate'
import { DateTime } from 'luxon'
import { useEffect, useRef, useState } from 'react'
import { CalendarIcon } from 'shared/icons'
import { CalendarButton } from './CalendarButton'
import { DateSelect } from 'shared/widgets/DateSelect/DateSelect'
import { useAppDispatch } from 'shared/hooks'
import { setShowMainBackDrop } from 'store/applicationSlice'
import { PickersDay, PickersDayProps } from '@mui/x-date-pickers'
import { isSameDay } from 'shared/lib/isSameDay'

interface Summary{

}

interface DailySummaryProps{
    className?: string
    summary: any
    spyId?: number
    onModeChange: (event: any) => void
    onDateChange: (event: any) => void
    onDateSelectOpenChange: (event: any) => void
}

export const DailySummary = (props: DailySummaryProps) => {
    const {
        className,
        summary,
        spyId,
        onModeChange,
        onDateChange,
        onDateSelectOpenChange
    } = props

    const [selectedValue, setSelectedValue] = useState<any>(undefined)
    const [selectedDialogValue, setSelectedDialogValue] = useState<any>(undefined)  
    const [dateSelectOpen, setDateSelectOpen] = useState<boolean>(false)
    const calendarButtonRef = useRef()

    const dailySummaryClasses = classNames(styles.DailySummary, className)

    const processDateString = (dateString: any) => {
        return DateTime.fromISO(dateString);
    };

    const findSuitableDay = () => {
        const closest = findClosestDate(summary.list, ({ checkinUtc }: { checkinUtc: any }) => processDateString(checkinUtc));
        return closest
    }

    useEffect(()=> {
        if (summary === undefined){
            setSelectedValue(undefined)
            return
        }

        if (summary.list.length === 0){
            setSelectedValue(undefined)
            return
        }

        if (selectedValue === undefined){
            const selectedValue = findSuitableDay()
            setSelectedValue(selectedValue)
            onDateChange(selectedValue)
        }
    }, [
        summary
    ])

    const performToggleDateSelectOpen = () => {
        setDateSelectOpen((prev) => {
            const value = !prev
            onDateSelectOpenChange?.(value)

            return value
        })
    }

    const handleCalendarButtonClick = () => {
        performToggleDateSelectOpen()
    }

    const handleSelectedDateClick = () => {
        performToggleDateSelectOpen()
    }

    let selectedDate = ''
    if (selectedValue?.checkinUtc){
        selectedDate = `(${DateTime.fromISO(selectedValue?.checkinUtc).toFormat('M/dd/yyyy')})`
    }
     
    const handleDateSelectClose = (event: any) => {
          setDateSelectOpen(false)
    }
    const dispatch = useAppDispatch()


    const handleDateSelectCancel = () => [
        setDateSelectOpen(false)
    ]

    useEffect(() => {
        dispatch(setShowMainBackDrop(dateSelectOpen))
    }, [
        dateSelectOpen
    ])

    const popperOptions = {
        placement: `bottom-end`,
        modifiers: [
                {
                    name: 'offset',
                    options: {
                        offset: [0, 10],
                    },
                },
            ]
    }

    const handleDayClick = (day: any) => {
        if (day === undefined){

            return
        }

        setSelectedDialogValue(day)
    }

    const handleDateSelectOk = () => {
        setSelectedValue(selectedDialogValue)
        setDateSelectOpen(false)
    }

    const customDay = (props: PickersDayProps<DateTime>) => {
        const updatedProps = { ...props, selected: false }
        if (summary === undefined) {
            return (
                <PickersDay {...updatedProps} />
            )
        }

        const dayHasData = summary.list
            .find((groupedByMonitorIdDataRow: any) => {
                return isSameDay(DateTime.fromISO(groupedByMonitorIdDataRow.checkinUtc), props.day);
            })
        
        let dayIsSameAsSelected = false
        if (selectedDialogValue !== undefined){
            dayIsSameAsSelected = isSameDay(DateTime.fromISO(selectedDialogValue.checkinUtc), props.day)
        }

        const dayClasses = classNames(styles.CustomDay, {
            [styles['CustomDay--HasData']]: dayHasData !== undefined,
            [styles['CustomDay--Selected']]: dayIsSameAsSelected === true,  
        })   

        return (
            <PickersDay 
                className={dayClasses} 
                {...updatedProps} 
                onClick={() => handleDayClick(dayHasData)}
            />
        )
    }

    const getNullableValue = (value: any) => {
        if (value === undefined){
            return ''
        }

        if (value === null){
            return 'n/a'
        }

        return value
    }

    return (
        <div className={dailySummaryClasses}>
            <div className={styles.DailySummary__Header}>
                <div className={styles.DailySummary__CalendarButtonContainer}>
                    <CalendarButton
                        onClick={handleCalendarButtonClick}
                        ref={calendarButtonRef}
                    />
                </div>
                <div className={styles.DailySummary__Heading0Container}>
                    <div className={styles.DailySummary__Heading0}>Daily Summary</div>
                </div>
                <div className={styles.DailySummary__DateContainer}>
                    <div 
                        className={styles.DailySummary__Date} 
                        onClick={handleSelectedDateClick}
                    >
                        {`${selectedDate}`}
                    </div>
                </div>
            </div>
                <hr className={styles.DailySummary__Divider} />
                <div className={styles.DailySummary__GridContainer}>
                    <div className={styles.DailySummary__Grid}>
                            <DailySummaryParam 
                                caption={`Competitors`}
                                value={`${selectedValue ? selectedValue?.competitors : ''}`}
                            />   

                            <DailySummaryParam 
                                caption={`Median price`}
                                value={`${selectedValue ? `$${selectedValue?.medianPrice}`: ''}`}
                            />

                            <DailySummaryParam 
                                caption={`Min price`}
                                value={`${selectedValue ? `$${selectedValue?.minPrice}`: ''}`}
                            />

                            <DailySummaryParam 
                                caption={`Max price`}
                                value={`${selectedValue ? `$${getNullableValue(selectedValue?.maxPrice)}`: ''}`}
                            />

                            <DailySummaryParam 
                                caption={`Supply`}
                                value={`${selectedValue ?  getNullableValue(selectedValue?.supply) : ''}`}
                            />
                            <DailySummaryParam 
                                caption={`Demand`}
                                value={`${selectedValue ? getNullableValue(selectedValue?.demand): ''}`}
                            /> 
                            {/* <DailySummaryParam 
                                caption={`Date updated`}
                                value={`${selectedValue ? DateTime.fromISO(selectedValue?.timestampUtc).toFormat('M/dd/yyyy hh:mm  a') : ''}`}
                            />   */}
                    </div>
                </div>
                <DateSelect
                    open={dateSelectOpen}
                    onClose={handleDateSelectClose}
                    onCancel={handleDateSelectCancel}
                    onOk={handleDateSelectOk}
                    anchorEl={calendarButtonRef}
                    popperOptions={popperOptions}
                    customDay={customDay}
                />
        </div>
    )
}